// ./src/components/features/Connectors/ConnectorSelector.tsx
import React, { useMemo, useEffect } from "react";
import { Select } from "@mantine/core";
import { STATUSES } from "@sdflc/utils";
import { Connector } from "@/utils/api";
import { useConnectors, usePremiumField } from "@/hooks";
import { useApp } from "@/contexts";
import {
  CONNECTOR_CATEGORY_LABELS,
  CONNECTOR_TYPE_LABELS,
} from "@/config/connectorCategories";

export interface ConnectorSelectorProps {
  /** Selected connector ID */
  value: string | null | undefined;

  /** Callback when selection changes - provides both ID and full connector object */
  onChange?: (connectorId: string | null, connector: Connector | null) => void;

  /** Filter by category */
  category?: string | null;

  /** Filter by type */
  types?: string[] | null;

  /** Whether to include inactive connectors */
  includeInactive?: boolean;

  /** Custom filter function */
  connectorFilter?: (connector: Connector) => boolean;

  /** Placeholder text */
  placeholder?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Field label */
  label?: string;

  /** Field description */
  description?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** If not null then it is a premium feature. If value is true the feature is enabled, otherwise disabled */
  premiumFeatureEnabled?: boolean | null | undefined;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const ConnectorSelector: React.FC<ConnectorSelectorProps> = ({
  value,
  onChange,
  category,
  types,
  includeInactive = false,
  connectorFilter,
  placeholder = "Select a connector",
  required = false,
  label,
  description,
  error,
  disabled,
  premiumFeatureEnabled,
  size = "md",
}) => {
  // Get current user from app context
  const { user } = useApp();
  const userId = user?.id || null;

  // Fetch connectors with userId for proper data isolation
  const { connectors, loading } = useConnectors({
    autoLoad: true,
    userId,
  });

  // Filter and build options
  const { filteredConnectors, options } = useMemo(() => {
    let filtered = connectors || [];

    // Filter by status
    if (!includeInactive) {
      filtered = filtered.filter((c) => c.status === STATUSES.ACTIVE);
    }

    // Filter by category
    if (category) {
      filtered = filtered.filter((c) => c.connectorCategory === category);
    }

    // Filter by type
    if (Array.isArray(types)) {
      filtered = filtered.filter((c) => types.includes(c.connectorType ?? ""));
    }

    // Apply custom filter
    if (connectorFilter) {
      filtered = filtered.filter(connectorFilter);
    }

    // Build select options
    const opts = filtered.map((connector) => {
      const categoryLabel = connector.connectorCategory
        ? CONNECTOR_CATEGORY_LABELS[connector.connectorCategory] ||
          connector.connectorCategory
        : "";
      const typeLabel = connector.connectorType
        ? CONNECTOR_TYPE_LABELS[connector.connectorType] ||
          connector.connectorType
        : "";

      // Build descriptive label: "Name (Category / Type) - Description"
      let optionLabel = connector.name || "Unnamed Connector";

      if (categoryLabel || typeLabel) {
        const typeInfo = [categoryLabel, typeLabel].filter(Boolean).join(" / ");
        optionLabel += ` (${typeInfo})`;
      }

      // Add status indicator for inactive
      if (connector.status !== STATUSES.ACTIVE) {
        const statusLabel =
          connector.status === STATUSES.PENDING
            ? "pending"
            : connector.status === STATUSES.DISABLED
            ? "disabled"
            : "inactive";
        optionLabel += ` [${statusLabel}]`;
      }

      // Add description snippet if available
      if (connector.description) {
        const descSnippet =
          connector.description.length > 30
            ? connector.description.substring(0, 30) + "..."
            : connector.description;
        optionLabel += ` - ${descSnippet}`;
      }

      return {
        value: connector.id || "",
        label: optionLabel,
      };
    });

    return { filteredConnectors: filtered, options: opts };
  }, [connectors, category, types, includeInactive, connectorFilter]);

  // Auto-select first option if required and no value
  useEffect(() => {
    if (required && !value && filteredConnectors.length > 0 && !loading) {
      const firstConnector = filteredConnectors[0];
      if (firstConnector.id) {
        onChange?.(firstConnector.id, firstConnector);
      }
    }
  }, [required, value, filteredConnectors, loading, onChange]);

  // Handle change
  const handleChange = (selectedId: string | null) => {
    if (selectedId) {
      const selectedConnector =
        filteredConnectors.find((c) => c.id === selectedId) || null;
      onChange?.(selectedId, selectedConnector);
    } else {
      onChange?.(null, null);
    }
  };

  const premium = usePremiumField({
    label,
    description,
    premiumFeatureEnabled,
  });

  return (
    <Select
      label={premium.label}
      description={premium.description}
      placeholder={loading ? "Loading connectors..." : placeholder}
      data={options}
      value={value ?? null}
      onChange={handleChange}
      required={required}
      withAsterisk={required}
      clearable={!required}
      disabled={premiumFeatureEnabled === false || disabled || loading}
      error={error}
      size={size}
      searchable
      nothingFoundMessage="No connectors found"
    />
  );
};
